/**
 * Google Tag Manager utility for tracking purchase events.
 *
 * Pushes a "purchase" event to the GTM dataLayer so it can be
 * forwarded to Google Ads (or any other GTM-connected destination).
 *
 * The dataLayer array is lazily initialised – if GTM has not yet
 * loaded the snippet, the push is queued and will be picked up as
 * soon as the container loads.
 */

import { trackSubscriptionCreated } from 'utils/oaiq';
import { analytics } from 'lib/analytics';

declare global {
  interface Window {
    dataLayer: Record<string, unknown>[];
  }
}

export interface PurchaseEventParams {
  /** Unique order / transaction ID (e.g. Stripe PaymentIntent ID) */
  transaction_id: string;
  /** Payment amount in major currency units (e.g. dollars, not cents) */
  value: number;
  /** ISO 4217 currency code – defaults to "USD" */
  currency?: string;
  /**
   * Stripe subscription ID, when the call site knows it. Preferred dedup key
   * for the OpenAI ChatGPT Ads pixel – see utils/oaiq.ts. Ignored by GTM.
   */
  subscription_id?: string;
  /** Which checkout flow produced this purchase (PostHog breakdown key) */
  checkout_entry?: string;
  /**
   * Skip the OpenAI pixel fan-out. For call sites that already report the
   * conversion via trackSubscriptionCreated under a different dedup key
   * (checkout-v2 keys on checkout_ref) — otherwise the pixel fires twice.
   */
  skipOaiq?: boolean;
}

/**
 * Push a purchase event to the GTM dataLayer.
 *
 * Safe to call even when GTM is not loaded – the event will be
 * queued in `window.dataLayer` and picked up when the container
 * initialises.
 */
export function trackPurchase({
  transaction_id,
  value,
  currency = 'USD',
  subscription_id,
  checkout_entry,
  skipOaiq = false
}: PurchaseEventParams): void {
  try {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'purchase',
      transaction_id,
      value,
      currency: currency.toUpperCase()
    });
    // Conversion event for the welcome-tour experiment. The active
    // $feature/welcome-tour flag value is stamped on automatically for
    // enrolled users — no variant plumbing needed here.
    analytics.purchase({ transaction_id, value, currency: currency.toUpperCase(), checkout_entry });
    console.log('[GTM] purchase event pushed', { transaction_id, value, currency });
  } catch (err) {
    // Non-critical – never let analytics break the app
    console.warn('[GTM] Failed to push purchase event', err);
  }

  // Fan out to the OpenAI ChatGPT Ads pixel from here rather than from each of
  // the six call sites: they all sit next to a stripe.confirmPayment call, and
  // every edit there is a chance to break checkout. trackSubscriptionCreated dedupes
  // on the event id internally and swallows its own errors, so this is safe to
  // call unconditionally — including from the try block's failure path above.
  if (!skipOaiq) trackSubscriptionCreated({ subscription_id, transaction_id, value, currency });
}
